import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Archive, BookOpenCheck, CheckCircle2, Clipboard, History, Loader2,
  Pencil, Plus, RefreshCcw, Search, ShieldCheck, X,
} from "lucide-react";

const emptyForm = {
  titulo: "",
  categoria: "texto_padrao",
  conteudo: "",
  tagsText: "",
  referencia: "",
  observacao_interna: "",
};

const categoryLabels = {
  texto_padrao: "Texto padrão",
  procedimento: "Procedimento",
  pop: "POP",
  checklist: "Checklist",
  requisito_legal: "Requisito legal",
  nota_tecnica: "Nota técnica",
  resposta_padrao: "Resposta padrão",
};

function normalizeForm(item) {
  if (!item) return { ...emptyForm };
  return {
    titulo: item.titulo || "",
    categoria: item.categoria || "texto_padrao",
    conteudo: item.conteudo || "",
    tagsText: (item.tags || []).join(", "),
    referencia: item.referencia || "",
    observacao_interna: item.observacao_interna || "",
  };
}

function payloadFromForm(form) {
  return {
    titulo: form.titulo.trim(),
    categoria: form.categoria,
    conteudo: form.conteudo,
    tags: form.tagsText.split(",").map((x) => x.trim()).filter(Boolean),
    referencia: form.referencia.trim(),
    observacao_interna: form.observacao_interna.trim(),
  };
}

export default function TechnicalLibrary() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState({ items: [], categories: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (category !== "all") params.set("categoria", category);
      if (approvedOnly) params.set("approved_only", "true");
      const r = await api.get(`/technical-library?${params.toString()}`);
      setData(r.data);
      if (selected?.id) {
        const fresh = (r.data.items || []).find((item) => item.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao carregar biblioteca técnica"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category, approvedOnly]); // eslint-disable-line

  const allCategories = useMemo(() => {
    const merged = new Set(["texto_padrao", "procedimento", "pop", "checklist", "requisito_legal", "nota_tecnica", "resposta_padrao", ...(data.categories || [])]);
    return Array.from(merged).sort();
  }, [data.categories]);

  const selectItem = (item) => {
    setSelected(item);
    setForm(normalizeForm(item));
    setEditing(false);
    setHistoryOpen(false);
    setHistory([]);
  };

  const startNew = () => {
    setSelected(null);
    setForm({ ...emptyForm });
    setEditing(true);
    setHistoryOpen(false);
  };

  const save = async () => {
    const payload = payloadFromForm(form);
    if (payload.titulo.length < 2 || !payload.conteudo.trim()) return toast.error("Informe título e conteúdo");
    setSaving(true);
    try {
      const r = selected?.id
        ? await api.put(`/technical-library/${selected.id}`, payload)
        : await api.post("/technical-library", payload);
      toast.success(selected?.id ? "Nova revisão salva; aprovação anterior foi retirada" : "Rascunho criado");
      setSelected(r.data);
      setForm(normalizeForm(r.data));
      setEditing(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar item"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!selected?.id) return;
    if (!window.confirm(`Aprovar a revisão v${selected.version} como padrão DEMoraes?`)) return;
    try {
      await api.post(`/technical-library/${selected.id}/approve`);
      toast.success("Conteúdo aprovado para uso como padrão DEMoraes");
      await load();
      const refreshed = (await api.get(`/technical-library?q=${encodeURIComponent(selected.titulo)}`)).data.items?.find((x) => x.id === selected.id);
      if (refreshed) selectItem(refreshed);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao aprovar conteúdo"); }
  };

  const archive = async () => {
    if (!selected?.id || !window.confirm("Arquivar este item? Ele deixará de aparecer nas buscas normais e nunca será usado pela IA.")) return;
    try {
      await api.post(`/technical-library/${selected.id}/archive`);
      toast.success("Item arquivado");
      setSelected(null);
      setForm({ ...emptyForm });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao arquivar item"); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success("Conteúdo copiado"); }
    catch { toast.error("Não foi possível copiar automaticamente"); }
  };

  const showHistory = async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get(`/technical-library/${selected.id}/history`);
      setHistory(r.data || []);
      setHistoryOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao carregar histórico"); }
  };

  return (
    <>
      <Header
        title="Biblioteca Técnica"
        subtitle="Padrões DEMoraes versionados, pesquisáveis e governados para equipe e IA"
        actions={<div className="flex flex-wrap gap-2">{isAdmin && <Button onClick={startNew}><Plus className="size-4" /> Novo conteúdo</Button>}<Button variant="outline" onClick={load}><RefreshCcw className="size-4" /> Atualizar</Button></div>}
      />
      <div className="page-container space-y-5" data-testid="technical-library-page">
        <div className="rounded-xl border border-primary/15 bg-primary/[0.04] p-4 text-sm text-muted-foreground">
          <ShieldCheck className="mr-2 inline size-4 text-primary" />
          Apenas conteúdos com status <strong className="text-foreground">Aprovado</strong> podem ser tratados pelo MCP/IA como padrão DEMoraes. Editar um item aprovado cria nova revisão e remove automaticamente sua aprovação até nova validação.
        </div>

        <Card><CardContent className="p-4"><div className="grid gap-3 lg:grid-cols-[1fr_220px_auto_auto]">
          <div className="relative"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Pesquisar título, conteúdo, tag ou referência…" /></div>
          <Select value={category} onValueChange={setCategory}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todas as categorias</SelectItem>{allCategories.map((cat) => <SelectItem key={cat} value={cat}>{categoryLabels[cat] || cat}</SelectItem>)}</SelectContent></Select>
          <label className="flex items-center gap-2 rounded-lg border px-3 text-sm"><input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} /> Só aprovados</label>
          <Button variant="outline" onClick={load}><Search className="size-4" /> Buscar</Button>
        </div></CardContent></Card>

        <div className="grid gap-5 xl:grid-cols-[.85fr_1.15fr]">
          <Card><CardContent className="p-0"><div className="border-b p-4"><div className="label-tech">Acervo</div><div className="mt-1 text-sm text-muted-foreground">{data.total || 0} resultado(s)</div></div><div className="max-h-[70vh] overflow-y-auto p-3">
            {loading && <div className="h-28 animate-pulse rounded-xl bg-muted/30" />}
            {!loading && (data.items || []).map((item) => <button key={item.id} type="button" onClick={() => selectItem(item)} className={`mb-2 w-full rounded-xl border p-4 text-left transition ${selected?.id === item.id ? "border-primary/40 bg-primary/[0.05]" : "border-border/70 hover:border-primary/20 hover:bg-muted/20"}`}>
              <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="truncate font-medium">{item.titulo}</div><div className="mt-1 text-xs text-muted-foreground">{categoryLabels[item.categoria] || item.categoria} · v{item.version || 1}</div></div><Badge variant={item.approved_at && item.status === "ativo" ? "secondary" : "outline"}>{item.approved_at && item.status === "ativo" ? "Aprovado" : "Rascunho"}</Badge></div>
              <p className="mt-3 line-clamp-3 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{item.conteudo}</p>
              {item.tags?.length > 0 && <div className="mt-3 flex flex-wrap gap-1">{item.tags.slice(0, 5).map((tag) => <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>)}</div>}
            </button>)}
            {!loading && !(data.items || []).length && <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">Nenhum conteúdo encontrado.</div>}
          </div></CardContent></Card>

          <Card><CardContent className="p-5 sm:p-6">
            {!selected && !editing ? <div className="grid min-h-72 place-items-center text-center"><div><BookOpenCheck className="mx-auto size-9 text-primary" /><h2 className="mt-3 font-semibold">Selecione um conteúdo</h2><p className="mt-1 text-sm text-muted-foreground">Abra um padrão para copiar, consultar histórico ou editar.</p></div></div> : <div className="space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"><div><div className="label-tech">{selected ? `Revisão v${selected.version || 1}` : "Novo rascunho"}</div><h2 className="mt-1 text-lg font-semibold">{selected?.titulo || "Novo conteúdo técnico"}</h2>{selected?.approved_at && selected.status === "ativo" && <div className="mt-2 flex items-center gap-2 text-xs text-primary"><CheckCircle2 className="size-4" /> Aprovado em {String(selected.approved_at).slice(0, 10)}</div>}</div><div className="flex flex-wrap gap-2">{selected && !editing && <Button size="sm" variant="outline" onClick={() => copy(selected.conteudo)}><Clipboard className="size-3.5" /> Copiar</Button>}{selected && <Button size="sm" variant="outline" onClick={showHistory}><History className="size-3.5" /> Histórico</Button>}{isAdmin && selected && !editing && <Button size="sm" variant="outline" onClick={() => { setForm(normalizeForm(selected)); setEditing(true); }}><Pencil className="size-3.5" /> Editar</Button>}{isAdmin && editing && <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setForm(normalizeForm(selected)); }}><X className="size-3.5" /> Cancelar</Button>}</div></div>

              {editing ? <div className="space-y-4">
                <div><Label>Título</Label><Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></div>
                <div className="grid gap-4 sm:grid-cols-2"><div><Label>Categoria</Label><Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{allCategories.map((cat) => <SelectItem key={cat} value={cat}>{categoryLabels[cat] || cat}</SelectItem>)}</SelectContent></Select></div><div><Label>Tags separadas por vírgula</Label><Input value={form.tagsText} onChange={(e) => setForm({ ...form, tagsText: e.target.value })} placeholder="NR-12, máquinas, proteção" /></div></div>
                <div><Label>Conteúdo técnico</Label><Textarea rows={16} value={form.conteudo} onChange={(e) => setForm({ ...form, conteudo: e.target.value })} placeholder="Texto técnico reutilizável…" /></div>
                <div><Label>Referência / fonte</Label><Input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} placeholder="Norma, procedimento, documento ou origem" /></div>
                <div><Label>Observação interna</Label><Textarea rows={3} value={form.observacao_interna} onChange={(e) => setForm({ ...form, observacao_interna: e.target.value })} placeholder="Contexto de uso, limitações ou orientações para a equipe" /></div>
                <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="size-4 animate-spin" /> : null} {selected ? "Salvar nova revisão" : "Criar rascunho"}</Button>
              </div> : selected && <div className="space-y-4">
                <div className="flex flex-wrap gap-2"><Badge variant="outline">{categoryLabels[selected.categoria] || selected.categoria}</Badge>{selected.tags?.map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}</div>
                <div className="rounded-xl border bg-muted/10 p-5"><div className="whitespace-pre-wrap text-sm leading-7">{selected.conteudo}</div></div>
                {selected.referencia && <div><div className="label-tech">Referência</div><div className="mt-1 text-sm text-muted-foreground">{selected.referencia}</div></div>}
                {selected.observacao_interna && <div><div className="label-tech">Observação interna</div><div className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{selected.observacao_interna}</div></div>}
                {isAdmin && <div className="flex flex-wrap gap-2 border-t pt-4">{!(selected.approved_at && selected.status === "ativo") && <Button onClick={approve}><CheckCircle2 className="size-4" /> Aprovar revisão v{selected.version}</Button>}<Button variant="outline" onClick={archive}><Archive className="size-4" /> Arquivar</Button></div>}
              </div>}

              {historyOpen && <div className="border-t pt-5"><div className="mb-3 flex items-center justify-between"><div><div className="label-tech">Histórico imutável</div><div className="mt-1 text-sm text-muted-foreground">Revisões anteriores preservadas antes de cada edição.</div></div><Button size="sm" variant="ghost" onClick={() => setHistoryOpen(false)}><X className="size-4" /></Button></div><div className="space-y-2">{history.map((rev) => <div key={rev.id} className="rounded-xl border p-3"><div className="flex items-center justify-between gap-2"><div className="text-sm font-medium">Versão {rev.version}</div><div className="text-xs text-muted-foreground">{String(rev.created_at || "").slice(0, 16).replace("T", " ")}</div></div><p className="mt-2 line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">{rev.snapshot?.conteudo || ""}</p></div>)}{!history.length && <p className="text-sm text-muted-foreground">Este item ainda não possui revisões anteriores.</p>}</div></div>}
            </div>}
          </CardContent></Card>
        </div>
      </div>
    </>
  );
}
